import type { Pool, PoolClient } from "pg";

import { salesOrderStatusForHubStorage } from "./sales-order-hub-status.js";

export interface SalesOrderCancelEligibility {
  allowed: boolean;
  reason: string | null;
  batchCount: number;
}

export const SALES_ORDER_CANCEL_BATCHES_BLOCK_MESSAGE =
  "This sales order has related manufacturing batches. Cancel is blocked while batches exist.";

export const SALES_ORDER_REVERT_BLOCKED_BY_PRODUCTION_MESSAGE =
  "This order has already been put into production and cannot be reverted to Pending until Manufacturing takes it out of production.";

export function salesOrderRevertToPendingProductionBlock(batchCount: number): string | null {
  const count = Math.max(0, Math.trunc(Number.isFinite(batchCount) ? batchCount : 0));
  if (count > 0) {
    return SALES_ORDER_REVERT_BLOCKED_BY_PRODUCTION_MESSAGE;
  }
  return null;
}

function normalizedSalesOrderStatus(value: unknown): string {
  return salesOrderStatusForHubStorage(value).trim().toLowerCase();
}

export function evaluateSalesOrderCancelEligibility(input: {
  status: string | null;
  mvId: string | number | null;
  batchCount: number;
}): SalesOrderCancelEligibility {
  const batchCount = Math.max(0, Math.trunc(input.batchCount));
  if (normalizedSalesOrderStatus(input.status) === "cancelled") {
    return {
      allowed: false,
      reason: "This sales order is already cancelled.",
      batchCount,
    };
  }
  if (normalizedSalesOrderStatus(input.status) !== "pending") {
    return {
      allowed: false,
      reason: "Only Pending hub sales orders can be cancelled.",
      batchCount,
    };
  }
  if (input.mvId != null) {
    return {
      allowed: false,
      reason: "Megaventory-linked sales orders cannot be cancelled from this app.",
      batchCount,
    };
  }
  if (batchCount > 0) {
    return {
      allowed: false,
      reason: SALES_ORDER_CANCEL_BATCHES_BLOCK_MESSAGE,
      batchCount,
    };
  }
  return {
    allowed: true,
    reason: null,
    batchCount: 0,
  };
}

export async function countSalesOrderRelatedBatches(
  db: Pool | PoolClient,
  salesOrderId: number,
): Promise<number> {
  const result = await db.query<{ batch_count: string | number }>(
    `
      select count(distinct public.batch_item.batch_id)::int as batch_count
        from public.batch_item
        join public.sales_order_item
          on public.sales_order_item.id = public.batch_item.sales_order_item_id
       where public.sales_order_item.sales_order_id = $1
    `,
    [salesOrderId],
  );
  const batchCount = Number(result.rows[0]?.batch_count ?? 0);
  return Number.isFinite(batchCount) && batchCount > 0 ? Math.trunc(batchCount) : 0;
}

export async function loadSalesOrderCancelEligibility(
  db: Pool | PoolClient,
  salesOrderId: number,
): Promise<SalesOrderCancelEligibility | null> {
  const orderResult = await db.query<{
    status: string | null;
    mv_id: string | number | null;
  }>(
    `
      select public.sales_order.status,
             public.sales_order.mv_id
        from public.sales_order
       where public.sales_order.id = $1
       limit 1
    `,
    [salesOrderId],
  );
  const order = orderResult.rows[0];
  if (order == null) {
    return null;
  }
  const batchCount = await countSalesOrderRelatedBatches(db, salesOrderId);
  return evaluateSalesOrderCancelEligibility({
    status: order.status,
    mvId: order.mv_id,
    batchCount,
  });
}

export function assertSalesOrderCancelAllowed(eligibility: SalesOrderCancelEligibility): void {
  if (!eligibility.allowed) {
    throw new Error(eligibility.reason ?? "This sales order cannot be cancelled.");
  }
}
